import React, { useEffect, useMemo, useState } from 'react'
import { adminRequest, uploadServiceIcon } from '../api/client'
import { Card, EmptyState, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'
import { BackLink, DetailGrid, SummaryGrid } from '../components/AdminCommon'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'

function slugify(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function categoryName(categories, id) {
  return categories.find((item) => item.id === id)?.name || '미분류'
}

function editorFrom(item = null, categories = [], count = 0) {
  return item
    ? { ...item, isNew: false, slugTouched: true, iconFile: null, iconPreview: item.iconImageUrl || '' }
    : {
      name: '', slug: '', description: '',
      categoryId: categories.find((row) => row.status === 'ACTIVE')?.id || null,
      iconMediaId: null, iconFile: null, iconPreview: '',
      status: 'ACTIVE', sortOrder: count + 1, isNew: true, slugTouched: false,
    }
}

function ServiceIconPreview({ item, size = 50 }) {
  return (
    <div className="service-icon-preview" style={{ width: size, height: size }}>
      {item?.iconImageUrl || item?.iconPreview
        ? <img src={item.iconImageUrl || item.iconPreview} alt="" />
        : <span aria-hidden="true">＋</span>}
    </div>
  )
}

function pickIcon(file, setEditor, setNotice) {
  if (!file) return
  if (!file.type.startsWith('image/')) {
    setNotice('서비스 아이콘은 이미지 파일만 등록할 수 있습니다.')
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    setNotice('서비스 아이콘은 5MB 이하로 등록해주세요.')
    return
  }
  setEditor((current) => ({ ...current, iconFile: file, iconPreview: URL.createObjectURL(file) }))
}

function ServiceEditor({ editor, setEditor, categories, onClose, onSubmit, saving, setNotice }) {
  return (
    <AdminFormModal
      open={Boolean(editor)}
      title={editor?.isNew ? '서비스 추가' : '서비스 수정'}
      description="사용자 화면에 노출되는 서비스명, 설명, 아이콘과 순서를 관리합니다."
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={saving ? '저장 중…' : '저장'}
      size="large"
    >
      {editor && <FormGrid>
        <FormField label="서비스명" required>
          <input
            required
            value={editor.name}
            onChange={(event) => setEditor({
              ...editor,
              name: event.target.value,
              slug: editor.slugTouched ? editor.slug : slugify(event.target.value),
            })}
          />
        </FormField>
        <FormField label="Slug" required hint="서비스 주소에 사용하는 영문 식별자입니다.">
          <input
            required
            value={editor.slug}
            onChange={(event) => setEditor({ ...editor, slug: slugify(event.target.value), slugTouched: true })}
          />
        </FormField>
        <FormField label="서비스 분류">
          <select value={editor.categoryId || ''} onChange={(event) => setEditor({ ...editor, categoryId: event.target.value || null })}>
            <option value="">미분류</option>
            {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </FormField>
        <FormField label="노출 순서">
          <input type="number" min="0" value={editor.sortOrder ?? 0} onChange={(event) => setEditor({ ...editor, sortOrder: event.target.value })} />
        </FormField>
        <FormField label="상태">
          <select value={editor.status || 'ACTIVE'} onChange={(event) => setEditor({ ...editor, status: event.target.value })}>
            <option value="ACTIVE">사용 중</option>
            <option value="INACTIVE">사용 중지</option>
          </select>
        </FormField>
        {!editor.isNew && <FormField label="서비스 ID">
          <input readOnly value={editor.publicCode || ''} />
        </FormField>}
        <FormField label="서비스 아이콘" wide required hint="권장 256×256px 이상의 PNG·WebP. 메인과 서비스 화면에 동일한 아이콘이 표시됩니다.">
          <div className="service-icon-editor">
            <ServiceIconPreview item={editor} size={72} />
            <div className="service-icon-editor-copy">
              <strong>{editor.iconPreview ? '현재 아이콘' : '아이콘을 등록해주세요.'}</strong>
              <p>밝은 배경에서도 선명한 심플 아이콘을 권장합니다.</p>
              <label className="button soft service-file-button">
                {editor.iconPreview ? '아이콘 변경' : '아이콘 선택'}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/avif" onChange={(event) => pickIcon(event.target.files?.[0], setEditor, setNotice)} />
              </label>
            </div>
          </div>
        </FormField>
        <FormField label="서비스 설명" wide>
          <textarea value={editor.description || ''} onChange={(event) => setEditor({ ...editor, description: event.target.value })} placeholder="사용자에게 보여줄 서비스 설명을 입력해주세요." />
        </FormField>
      </FormGrid>}
    </AdminFormModal>
  )
}

function DeleteServiceModal({ target, deleting, onClose, onSubmit }) {
  return (
    <AdminFormModal
      open={Boolean(target)}
      title="서비스 삭제"
      description="삭제 후에는 서비스 목록에서 복구할 수 없습니다."
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={deleting ? '삭제 중…' : '삭제'}
      submitTone="danger-soft"
    >
      {target && <div className="service-delete-confirm">
        <ServiceIconPreview item={target} size={58} />
        <div>
          <strong>{target.name}</strong>
          <p>연결된 디렉터, 견적 요청, 견적 질문 등 실제 데이터가 있으면 안전을 위해 삭제가 차단됩니다. 노출만 중단하려면 ‘사용 중지’로 변경해주세요.</p>
        </div>
      </div>}
    </AdminFormModal>
  )
}

export function ServicesAdminV2({ navigate }) {
  const [items, setItems] = useState([])
  const [categories, setCategories] = useState([])
  const [search, setSearch] = useState('')
  const [editor, setEditor] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const [servicesResponse, categoriesResponse] = await Promise.all([
        adminRequest('/api/v1/admin/services'),
        adminRequest('/api/v1/admin/categories'),
      ])
      setItems(servicesResponse?.data || [])
      setCategories(categoriesResponse?.data || [])
    } catch (nextError) {
      setError(nextError?.message || '서비스 목록을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const rows = useMemo(() => items
    .filter((item) => !search || [
      item.name, item.publicCode, item.slug, item.description, categoryName(categories, item.categoryId),
    ].join(' ').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)), [items, categories, search])

  const openEditor = (item = null) => {
    setNotice('')
    setEditor(editorFrom(item, categories, items.length))
  }

  const save = async (event) => {
    event.preventDefault()
    if (saving) return
    setSaving(true)
    setNotice('')
    try {
      let iconMediaId = editor.iconMediaId || null
      if (editor.iconFile) iconMediaId = (await uploadServiceIcon(editor.iconFile)).mediaId
      if (!iconMediaId) throw new Error('서비스 아이콘을 등록해주세요.')
      const body = {
        slug: editor.slug.trim(),
        name: editor.name.trim(),
        description: editor.description?.trim() || null,
        categoryId: editor.categoryId || null,
        iconMediaId,
        status: editor.status,
        sortOrder: Number(editor.sortOrder || 0),
      }
      if (editor.isNew) {
        await adminRequest('/api/v1/admin/services', { method: 'POST', body })
      } else {
        await adminRequest(`/api/v1/admin/services/${encodeURIComponent(editor.publicCode || editor.id)}`, {
          method: 'PUT', headers: { 'If-Match': String(editor.version) }, body,
        })
      }
      setEditor(null)
      setNotice(editor.isNew ? '서비스를 추가했습니다.' : '서비스 정보를 수정했습니다.')
      await load()
    } catch (nextError) {
      setNotice(nextError?.message || '서비스 저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (event) => {
    event.preventDefault()
    if (!deleteTarget || deleting) return
    setDeleting(true)
    setNotice('')
    try {
      await adminRequest(`/api/v1/admin/services/${encodeURIComponent(deleteTarget.publicCode || deleteTarget.id)}`, {
        method: 'DELETE',
        headers: { 'If-Match': String(deleteTarget.version) },
      })
      const deletedName = deleteTarget.name
      setDeleteTarget(null)
      setNotice(`${deletedName} 서비스를 삭제했습니다.`)
      await load()
    } catch (nextError) {
      setDeleteTarget(null)
      setNotice(nextError?.message || '서비스 삭제에 실패했습니다.')
    } finally {
      setDeleting(false)
    }
  }

  return <>
    <PageTitle
      eyebrow="SERVICE CATALOG"
      title="서비스 관리"
      description="메인과 서비스 찾기, 견적 요청에서 공통으로 사용하는 서비스 정보를 관리합니다."
      actions={<button className="button primary" onClick={() => openEditor()}>서비스 추가</button>}
    />
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
    <Card className="table-card">
      <div className="table-toolbar">
        <SearchField value={search} onChange={setSearch} placeholder="서비스명, ID, 분류 검색" />
      </div>
      <div className="table-result-bar">
        <span>서비스 <strong>{rows.length}</strong>개</span>
        <small>아이콘 · 설명 · 노출 순서를 한 곳에서 관리합니다.</small>
      </div>
      {loading
        ? <div className="service-loading">서비스 목록을 불러오는 중입니다.</div>
        : error
          ? <EmptyState title="서비스 조회 실패" description={error} />
          : rows.length
            ? <div className="table-scroll"><table className="data-table service-catalog-table">
              <thead><tr><th>서비스</th><th>분류</th><th>노출 순서</th><th>상태</th><th>수정일</th><th>관리</th></tr></thead>
              <tbody>{rows.map((item) => <tr className="clickable-row" key={item.id} onClick={() => navigate(`/settings/services/${item.publicCode || item.id}`)}>
                <td>
                  <div className="service-table-identity">
                    <ServiceIconPreview item={item} size={46} />
                    <div>
                      <strong>{item.name}</strong>
                      <p>{item.description || '서비스 설명이 없습니다.'}</p>
                      <small>{item.publicCode || '-'} · {item.slug}</small>
                    </div>
                  </div>
                </td>
                <td>{categoryName(categories, item.categoryId)}</td>
                <td><span className="service-order-chip">{item.sortOrder}</span></td>
                <td><StatusBadge status={item.status} /></td>
                <td>{formatDate(item.updatedAt, true)}</td>
                <td>
                  <div className="service-row-actions" onClick={(event) => event.stopPropagation()}>
                    <button type="button" className="text-button" onClick={() => openEditor(item)}>수정</button>
                    <button type="button" className="text-button danger" onClick={() => setDeleteTarget(item)}>삭제</button>
                  </div>
                </td>
              </tr>)}</tbody>
            </table></div>
            : <EmptyState title="등록된 서비스가 없습니다." description="서비스 추가 버튼으로 첫 서비스를 등록해주세요." />}
    </Card>
    <ServiceEditor editor={editor} setEditor={setEditor} categories={categories} onClose={() => !saving && setEditor(null)} onSubmit={save} saving={saving} setNotice={setNotice} />
    <DeleteServiceModal target={deleteTarget} deleting={deleting} onClose={() => !deleting && setDeleteTarget(null)} onSubmit={remove} />
  </>
}

export function ServiceDetailAdminV2({ id, navigate }) {
  const [item, setItem] = useState(null)
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let disposed = false
    Promise.all([
      adminRequest(`/api/v1/admin/services/${encodeURIComponent(id)}`),
      adminRequest('/api/v1/admin/quote-questions'),
    ]).then(([serviceResponse, questionResponse]) => {
      if (disposed) return
      const service = serviceResponse?.data || null
      setItem(service)
      setQuestions((questionResponse?.data || []).filter((question) => question.serviceId === service?.id))
    }).catch((next) => {
      if (!disposed) setError(next?.message || '서비스를 찾을 수 없습니다.')
    }).finally(() => {
      if (!disposed) setLoading(false)
    })
    return () => { disposed = true }
  }, [id])

  if (loading) return <><BackLink onClick={() => navigate('/settings/services')} /><Card><p>서비스 정보를 불러오는 중입니다.</p></Card></>
  if (error || !item) return <><BackLink onClick={() => navigate('/settings/services')} /><Card><EmptyState title="서비스 조회 실패" description={error || '서비스를 찾을 수 없습니다.'} /></Card></>

  return <>
    <BackLink onClick={() => navigate('/settings/services')} />
    <PageTitle eyebrow="SERVICE DETAIL" title={item.name} description="사용자 노출 정보와 연결된 견적 질문을 확인합니다." />
    <SummaryGrid items={[
      { label: '상태', value: <StatusBadge status={item.status} /> },
      { label: '서비스 ID', value: item.publicCode },
      { label: 'Slug', value: item.slug },
      { label: '견적 질문', value: `${questions.length}개` },
    ]} />
    <Card title="서비스 정보"><DetailGrid items={[
      { label: '서비스명', value: item.name },
      { label: '설명', value: item.description || '-', wide: true },
      { label: '노출 순서', value: item.sortOrder },
      { label: '수정일', value: formatDate(item.updatedAt, true) },
    ]} /></Card>
  </>
}
